/**
 * 도감 모듈 정보 팝업 조립 — moduleInfoModal.ts의 정의(definition) 기준 쌍둥이.
 * 도감은 인스턴스가 아니라 정의 1종을 클릭하므로, 미보유 모듈도 열 수 있어야 한다.
 * 미발견(discovered=false ∧ 보유 0) 모듈은 locked로 열어 효과 수치/확률을 ???로 가린다(이름·등급·모양 풀은 공개).
 */
import { buildLauncherInfoData, buildModuleInfoDataFromDef } from "./tabData.js";
import { REWARD_ICON_KEY } from "./rewardIcons.js";
import { EconomyController } from "../systems/EconomyController.js";
import { spaceshipIdFromLauncherCodexId } from "../data/codex.js";
import { isPieceUnlocked } from "../data/modulePieces.js";
import { spaceshipById } from "../data/spaceshipData.js";
import { t } from "../i18n/i18n.js";
import type { ModalHost } from "./ModalHost.js";
import type { ModuleInfoAction } from "./ModuleInfoPopup.js";
import type { GameContext } from "../app/GameContext.js";

export interface CodexInfoModalDeps {
  /** 팝업 열기·닫기·내용 갱신을 담당하는 ModalHost. */
  modals: ModalHost;
  /** 모듈 레지스트리·인벤토리 접근용 게임 컨텍스트. */
  ctx: GameContext;
  /** 현재 화면 크기 — 팝업 크기·위치 계산에 사용. */
  screen: () => { width: number; height: number };
  /** 발견된 정의 ID 집합 — discovered 판정용. */
  discovered: () => ReadonlySet<string>;
  /** 모듈 레벨업 — 도감에서도 강화석으로 레벨업. 미발견이어도 조각이 해금됐으면 선강화 가능. */
  upgradeModule?: (moduleId: string) => void;
}

/**
 * 도감 탭에서 모듈 카드를 클릭할 때 정보 팝업을 연다.
 * 레지스트리에 정의가 없으면 조용히 종료. 미발견·미보유 모듈은 `locked=true`로 열어
 * 효과 수치를 `???`로 가린다(이름·등급·모양 풀은 공개).
 * @param deps - 팝업·컨텍스트·화면 크기·발견 집합 의존성 묶음.
 * @param definitionId - 클릭한 모듈 정의 ID.
 */
export function openCodexModuleInfo(deps: CodexInfoModalDeps, definitionId: string): void {
  const spaceshipId = spaceshipIdFromLauncherCodexId(definitionId);
  if (spaceshipId) {
    const spaceship = spaceshipById(spaceshipId);
    if (!spaceship) return;
    const screen = deps.screen();
    deps.modals.openModuleInfo({
      screenWidth: screen.width,
      screenHeight: screen.height,
      onClose: () => deps.modals.closeModuleInfo(),
    });
    deps.modals.updateModuleInfo(buildLauncherInfoData(spaceship));
    return;
  }
  if (definitionId === "launcher") {
    const screen = deps.screen();
    deps.modals.openModuleInfo({
      screenWidth: screen.width,
      screenHeight: screen.height,
      onClose: () => deps.modals.closeModuleInfo(),
    });
    deps.modals.updateModuleInfo(buildLauncherInfoData());
    return;
  }
  const def = deps.ctx.moduleRegistry().getDefinition(definitionId);
  if (!def) return;
  const owned = deps.ctx
    .inventory()
    .allInstances()
    .filter((i) => i.definitionId === definitionId);
  const bestChapter = deps.ctx.chapter().bestChapter;
  const locked = !deps.discovered().has(definitionId) && owned.length === 0;
  const hasUnlockedPiece = def.pieces.some((p) => isPieceUnlocked(p, bestChapter));
  const screen = deps.screen();
  // 강화 버튼 — 발견/보유 전이어도 조각이 1개 이상 해금됐으면 선강화 가능. 레벨은 moduleId별 플레이어 자산.
  const upgradeAction =
    (!locked || hasUnlockedPiece) && deps.upgradeModule
      ? buildCodexUpgradeAction(deps, definitionId)
      : undefined;
  deps.modals.openModuleInfo({
    screenWidth: screen.width,
    screenHeight: screen.height,
    onClose: () => deps.modals.closeModuleInfo(),
    action: upgradeAction,
  });
  deps.modals.updateModuleInfo(
    buildModuleInfoDataFromDef(
      def,
      deps.ctx.playerModuleLevels().level(definitionId),
      locked,
      bestChapter,
    ),
  );
}

/**
 * 도감 강화 버튼 action — 클릭 시 확인 모달(UpgradeConfirmPopup)을 띄워 현재→다음 레벨/스탯 + 비용 표시.
 * 버튼은 항상 활성(정보 확인 목적), 강화석 게이트는 확인 모달 Yes가 담당. Yes에서만 강화 + 도감 팝업 재오픈.
 */
function buildCodexUpgradeAction(deps: CodexInfoModalDeps, definitionId: string): ModuleInfoAction {
  const level = deps.ctx.playerModuleLevels().level(definitionId);
  const stoneCost = EconomyController.upgradeStoneCost(level);
  return {
    label: t("moduleInfo.upgradeButton", { stone: stoneCost }),
    enabled: true,
    iconKey: REWARD_ICON_KEY.upgradeStone, // 강화석 아이콘 — 라벨 "영구 강화 N" 왼쪽
    iconSize: 14,
    onAction: () => {
      const def = deps.ctx.moduleRegistry().getDefinition(definitionId);
      if (!def) return;
      const screen = deps.screen();
      const best = deps.ctx.chapter().bestChapter;
      deps.modals.openUpgradeConfirm({
        screenWidth: screen.width,
        screenHeight: screen.height,
        moduleName: t(`module.${definitionId}.name`),
        currentLevel: level,
        nextLevel: level + 1,
        current: buildModuleInfoDataFromDef(def, level, false, best),
        next: buildModuleInfoDataFromDef(def, level + 1, false, best),
        stoneCost,
        ownedStone: deps.ctx.upgradeInv().universalUpgradeStone,
        costIconKey: REWARD_ICON_KEY.upgradeStone,
        canAfford: () => deps.ctx.upgradeInv().universalUpgradeStone >= stoneCost,
        onConfirm: () => {
          deps.upgradeModule?.(definitionId);
          deps.modals.closeUpgradeConfirm();
          deps.modals.closeModuleInfo();
          openCodexModuleInfo(deps, definitionId);
        },
        onCancel: () => deps.modals.closeUpgradeConfirm(),
      });
    },
  };
}
